<template>
  <Teleport to="body">
    <Transition name="build-update-slide">
      <aside
        v-if="isUpdateAvailable"
        class="build-update-notice"
        role="status"
        aria-live="polite"
        data-testid="build-update-notice"
      >
        <div class="build-update-notice__icon" aria-hidden="true">
          <i class="zmdi zmdi-refresh-sync" />
        </div>
        <div class="build-update-notice__body">
          <p class="build-update-notice__title">{{ t('buildUpdate.title') }}</p>
          <p class="build-update-notice__message">
            {{ t('buildUpdate.message') }}
          </p>
        </div>
        <BaseButton
          monochrome
          size="sm"
          class="build-update-notice__action"
          data-testid="build-update-reload"
          @click="reloadPage"
        >
          {{ t('buildUpdate.action') }}
        </BaseButton>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import BaseButton from '@/shared/components/base/BaseButton.vue'
import { useBuildVersionCheck } from '@/composables/useBuildVersionCheck'

const { t } = useI18n()
const { isUpdateAvailable, reloadPage } = useBuildVersionCheck()
</script>

<style scoped lang="scss">
.build-update-notice {
  position: fixed;
  left: 50%;
  bottom: var(--ds-space-xl);
  transform: translateX(-50%);
  // Below payment notices and blocking dialogs.
  z-index: 200;
  display: flex;
  align-items: center;
  gap: var(--ds-space-md);
  width: min(520px, calc(100vw - 32px));
  padding: var(--ds-space-md) var(--ds-space-lg);
  box-sizing: border-box;
  border: 1px solid var(--ds-border-strong);
  border-radius: var(--ds-radius-card);
  background: var(--ds-surface-raised);
  color: var(--ds-text);
  box-shadow: var(--ds-elevation-3);

  @media (max-width: 768px) {
    bottom: calc(
      88px + var(--ds-space-md) + env(safe-area-inset-bottom, 0px)
    );
  }

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: var(--ds-radius-pill);
    background: var(--ds-accent-soft);
    color: var(--ds-accent);
    font-size: var(--ds-text-size-body-lg);
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: var(--ds-text-size-body);
    font-weight: var(--ds-weight-semibold);
    line-height: var(--ds-leading-tight);
    color: var(--ds-text);
  }

  &__message {
    margin: var(--ds-space-xs) 0 0;
    font-size: var(--ds-text-size-caption);
    font-weight: var(--ds-weight-regular);
    line-height: var(--ds-leading-normal);
    color: var(--ds-text-secondary);
  }

  &__action {
    flex-shrink: 0;

    @media (max-width: 480px) {
      width: 100%;
    }
  }
}

.build-update-slide-enter-active,
.build-update-slide-leave-active {
  transition:
    transform var(--ds-motion-base) var(--ds-ease-game),
    opacity var(--ds-motion-base) var(--ds-ease-game);
}

.build-update-slide-enter-from,
.build-update-slide-leave-to {
  transform: translate(-50%, 16px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .build-update-slide-enter-active,
  .build-update-slide-leave-active {
    transition: none;
  }
}
</style>
